package dev.kuml.portal.web.i18n

import kotlinx.browser.window

/** `localStorage` key R-18's explicit language choice is persisted under -- see this wave's plan,
 * decision E-3 (clientside-only, no account-wide setting in this wave). */
private const val STORAGE_KEY: String = "kportal.lang"

/**
 * Thin, defensive wrapper around `localStorage` for the persisted language choice. Every access is
 * wrapped in `runCatching {}` -- Safari private mode and "block all cookies" throw on `localStorage`
 * access, and this is a pure UI-preference nicety, never worth crashing the app over (see this
 * wave's plan §3, "Robustheit (Pflicht)").
 */
public object LanguageStore {
    /** The persisted [Language], or `null` if nothing is stored, storage is unavailable, or the
     * stored value is anything other than exactly `"de"`/`"en"` -- garbage in storage (a stale
     * schema, a browser extension, manual tampering) must fall back to detection, never crash and
     * never silently coerce to a language nobody chose. */
    public fun load(): Language? =
        runCatching { window.localStorage.getItem(STORAGE_KEY) }.getOrNull()?.let { Language.fromStorageValue(it) }

    /** Best-effort persist -- a thrown/failed write leaves the in-memory [LanguageState.current]
     * as the only source of truth for the rest of this session, which is an acceptable, silent
     * degradation (see class KDoc). */
    public fun save(language: Language) {
        runCatching { window.localStorage.setItem(STORAGE_KEY, language.storageValue) }
    }
}
